import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, X, Trash2, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const QUOTE_STATUS = {
  borrador: "Borrador",
  enviado: "Enviado",
  aprobado: "Aprobado",
  rechazado: "Rechazado",
  facturado: "Facturado",
};

const QUOTE_STATUS_STYLE = {
  borrador: "bg-neutral-100 text-neutral-700 border-neutral-300",
  enviado: "bg-blue-50 text-blue-800 border-blue-300",
  aprobado: "bg-emerald-50 text-emerald-800 border-emerald-300",
  rechazado: "bg-red-50 text-red-800 border-red-300",
  facturado: "bg-[#6E8B3D] text-white border-[#556C2E]",
};

function calcTotals(items, taxRate) {
  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = subtotal * ((Number(taxRate) || 0) / 100);
  return { subtotal, tax, total: subtotal + tax };
}

export default function Quotes() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    const r = await api.get("/quotes");
    setList(r.data);
  };

  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
  }, []);

  return (
    <div className="p-6 lg:p-10">
      <header className="flex items-end justify-between pb-6 gap-4 flex-wrap border-b border-dashed border-[#D5D7CE]">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
            <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Documentos
          </div>
          <h1 className="font-display text-4xl lg:text-[38px] font-black mt-2 tracking-tight">Cotizaciones</h1>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="quote-new"
          className="ds-btn primary"
        >
          <Plus size={15} /> Nueva cotización
        </button>
      </header>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 ds-card max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 border-b border-[#E5E5E0] font-display font-bold text-sm">
            Listado
          </div>
          {list.map((q) => {
            const { total } = calcTotals(q.items, q.tax_rate);
            return (
              <button
                key={q.id}
                onClick={() => setActive(q)}
                data-testid={`quote-row-${q.code}`}
                className={`w-full text-left px-4 py-3 border-b border-[#E5E5E0] hover:bg-[#F7F7F4] transition-colors ${
                  active?.id === q.id ? "bg-[#F7F7F4]" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-[#525252]">{q.code}</span>
                  <span className={`px-2 py-0.5 text-[10px] font-mono uppercase border ${QUOTE_STATUS_STYLE[q.status]}`}>
                    {QUOTE_STATUS[q.status]}
                  </span>
                </div>
                <div className="font-semibold text-sm mt-1">{q.customer_name}</div>
                <div className="font-mono text-xs text-[#525252] mt-1">€{total.toFixed(2)}</div>
              </button>
            );
          })}
          {list.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-[#525252]">Sin cotizaciones</div>
          )}
        </div>

        <div className="lg:col-span-2">
          {active ? (
            <QuoteDetail key={active.id} initial={active} onUpdated={(q) => { setActive(q); load(); }} />
          ) : (
            <div className="ds-card p-10 text-center text-sm text-[#525252]">
              Selecciona una cotización para ver el detalle
            </div>
          )}
        </div>
      </div>

      {open && (
        <NewQuoteModal
          customers={customers}
          onClose={() => setOpen(false)}
          onCreated={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function QuoteDetail({ initial, onUpdated }) {
  const [q, setQ] = useState(initial);
  const [items, setItems] = useState(initial.items);
  const [taxRate, setTaxRate] = useState(initial.tax_rate);
  const [status, setStatus] = useState(initial.status);
  const totals = calcTotals(items, taxRate);

  const updateItem = (i, key, val) => {
    const next = items.slice();
    next[i] = { ...next[i], [key]: key === "description" ? val : Number(val) };
    setItems(next);
  };

  const addItem = () => setItems([
    ...items,
    {
      _uid: `qitem-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      description: "", quantity: 1, unit_price: 0,
    },
  ]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      const r = await api.put(`/quotes/${q.id}`, { items, tax_rate: Number(taxRate), status });
      setQ(r.data);
      toast.success("Cotización guardada");
      onUpdated(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const convertToInvoice = async () => {
    try {
      const r = await api.put(`/quotes/${q.id}`, { status: "facturado" });
      setStatus("facturado");
      toast.success("Convertida a factura");
      onUpdated(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="ds-card">
      <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
        <div>
          <div className="font-mono text-xs text-[#525252]">{q.code}</div>
          <div className="font-display font-bold">{q.customer_name}</div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="quote-status-select"
            className="px-2 py-1 text-xs font-mono uppercase border border-[#E5E5E0] focus:border-[#6E8B3D] rounded-none"
          >
            {Object.entries(QUOTE_STATUS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b border-[#E5E5E0]">
              <th className="py-2">Descripción</th>
              <th className="py-2 w-20 text-right">Cant.</th>
              <th className="py-2 w-28 text-right">P. Unit</th>
              <th className="py-2 w-28 text-right">Total</th>
              <th className="py-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it._uid || `qitem-${i}`} className="border-b border-[#E5E5E0]">
                <td className="py-1">
                  <input
                    value={it.description}
                    onChange={(e) => updateItem(i, "description", e.target.value)}
                    data-testid={`quote-item-desc-${i}`}
                    placeholder="Repuesto / mano de obra..."
                    className="w-full bg-transparent outline-none px-1 py-1 text-sm border-b border-transparent focus:border-[#6E8B3D]"
                  />
                </td>
                <td className="py-1">
                  <input
                    type="number"
                    value={it.quantity}
                    onChange={(e) => updateItem(i, "quantity", e.target.value)}
                    className="w-full bg-transparent outline-none px-1 py-1 text-sm text-right font-mono border-b border-transparent focus:border-[#6E8B3D]"
                  />
                </td>
                <td className="py-1">
                  <input
                    type="number"
                    step="0.01"
                    value={it.unit_price}
                    onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                    className="w-full bg-transparent outline-none px-1 py-1 text-sm text-right font-mono border-b border-transparent focus:border-[#6E8B3D]"
                  />
                </td>
                <td className="py-1 text-right font-mono text-sm">
                  €{((it.quantity || 0) * (it.unit_price || 0)).toFixed(2)}
                </td>
                <td>
                  <button onClick={() => removeItem(i)} className="p-1 text-[#525252] hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          onClick={addItem}
          data-testid="quote-add-item"
          className="mt-3 inline-flex items-center gap-2 border border-dashed border-[#E5E5E0] hover:border-[#111110] px-3 py-1.5 text-xs text-[#525252]"
        >
          <Plus size={12} /> Agregar línea
        </button>

        <div className="mt-6 flex justify-end">
          <div className="w-72 space-y-1.5 font-mono text-sm">
            <div className="flex justify-between">
              <span className="text-[#525252]">Subtotal</span>
              <span>€{totals.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <label className="text-[#525252]">Impuesto</label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  className="w-12 text-right ds-card px-1 py-0.5 rounded-none"
                />
                <span className="text-[#525252]">%</span>
                <span className="w-16 text-right">€{totals.tax.toFixed(2)}</span>
              </div>
            </div>
            <div className="flex justify-between border-t-2 border-[#111110] pt-2 font-display font-black text-base">
              <span>TOTAL</span>
              <span>€{totals.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            onClick={save}
            data-testid="quote-save"
            className="border border-[#111110] hover:bg-[#111110] hover:text-white px-4 py-2 text-sm font-semibold"
          >
            Guardar
          </button>
          {status !== "facturado" && (
            <button
              onClick={convertToInvoice}
              data-testid="quote-convert"
              className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-sm font-semibold"
            >
              <FileCheck2 size={14} /> Convertir a factura
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NewQuoteModal({ customers, onClose, onCreated }) {
  const [customer_id, setCustomerId] = useState("");
  const [notes, setNotes] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/quotes", { customer_id, items: [], tax_rate: 0, notes });
      toast.success("Cotización creada");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#111110] shadow-hard w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div className="font-display font-bold">Nueva cotización</div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Cliente *</label>
            <select
              required
              value={customer_id}
              onChange={(e) => setCustomerId(e.target.value)}
              data-testid="quote-form-customer"
              className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
            >
              <option value="">— Seleccionar —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Notas</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
            />
          </div>
          <button
            type="submit"
            data-testid="quote-form-submit"
            className="w-full bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2.5 text-sm font-semibold"
          >
            Crear cotización
          </button>
        </form>
      </div>
    </div>
  );
}
